// UI-SPEC §"Login screen" (UI-04) — centered card max-w-sm on the page
// background; serif display title "Entrar no Jurion"; body, email +
// password inputs, primary CTA "Entrar"; "Esqueceu sua senha?" link;
// validation/error toasts per UI-SPEC §Copywriting Contract.
'use client';

import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { normalizeInternalReturnPath } from '@/shared/client/auth-response';
import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import { Label } from '@/shared/ui/components/label';
import { Toaster, toast } from '@/shared/ui/components/sonner';
import { Logo } from '@/shared/ui/layout/Logo';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accessUpdated, setAccessUpdated] = useState(false);
  const [awaitingTwoFactor, setAwaitingTwoFactor] = useState(false);
  const [totpCode, setTotpCode] = useState('');

  async function resumeAfterLogin(rawDestination: string | null): Promise<void> {
    const destination = normalizeInternalReturnPath(rawDestination) ?? '/';
    const response = await fetch(destination, {
      method: 'GET',
      headers: { 'X-Jurion-Return-Preflight': '1' },
      credentials: 'include',
      cache: 'no-store',
    });
    window.location.replace(response.status === 204 ? destination : '/?return-denied=1');
  }

  // Post-redemption success (Phase 43 / D-03 — no auto-login). The
  // /definir-senha page redirects here with ?definido=1; show the success
  // toast once and clean the param so a reload doesn't repeat it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'access-updated') {
      setAccessUpdated(true);
      const redirect = normalizeInternalReturnPath(params.get('redirect'));
      const clean = new URL('/login', window.location.origin);
      if (redirect) clean.searchParams.set('redirect', redirect);
      window.history.replaceState({}, '', `${clean.pathname}${clean.search}`);
    }
    if (params.get('definido') === '1') {
      toast.success('Senha definida — faça login.');
      window.history.replaceState({}, '', '/login');
    }
    // Auto-login fallback (Phase 65 / LOGIN-03): /cadastro redireciona para cá
    // com ?cadastrado=1 quando a conta foi criada mas o auto-login falhou.
    if (params.get('cadastrado') === '1') {
      toast.success('Conta criada. Faça login para continuar.');
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch('/api/v1/auth/sign-in/email', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });
      if (res.ok) {
        const body = (await res
          .clone()
          .json()
          .catch(() => null)) as {
          twoFactorRedirect?: boolean;
        } | null;
        if (body?.twoFactorRedirect) {
          setAwaitingTwoFactor(true);
          return;
        }
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get('redirect') ?? '/';
        await resumeAfterLogin(redirect);
        return;
      }
      let errorText = '';
      try {
        const json = (await res.json()) as { message?: string; code?: string };
        errorText = json.message ?? json.code ?? '';
      } catch {
        // ignore
      }
      // Specific code for "no active escritorio" case per UI-SPEC. Better-Auth
      // returns a generic 401 — we surface the spec'd generic message and
      // log the underlying error to console for debugging.
      if (errorText.includes('escritório') || errorText.includes('escritorio')) {
        toast.error(
          'Sua conta não está vinculada a nenhum escritório ativo. Entre em contato com o administrador.',
        );
      } else {
        // T-01-02-01: never reveal which field is wrong.
        toast.error('E-mail ou senha incorretos.');
      }
    } catch {
      toast.error('Não foi possível conectar. Tente novamente.');
    } finally {
      setBusy(false);
    }
  }

  async function onTwoFactorSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    try {
      const response = await fetch('/api/v1/auth/two-factor/verify-totp', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code: totpCode }),
        credentials: 'include',
      });
      if (!response.ok) {
        toast.error('Código de verificação inválido.');
        return;
      }
      const params = new URLSearchParams(window.location.search);
      await resumeAfterLogin(params.get('redirect'));
    } catch {
      toast.error('Não foi possível verificar o código. Tente novamente.');
    } finally {
      setBusy(false);
    }
  }

  function onForgotPassword(e: React.MouseEvent) {
    e.preventDefault();
    toast.info('Em breve. Por enquanto, fale com o administrador do escritório.');
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <main className="w-full max-w-sm">
        <div className="rounded-lg border border-border bg-card shadow-sm p-8 flex flex-col gap-6">
          <div className="flex flex-col items-center gap-6">
            <Logo className="h-8 w-auto text-foreground" />
            <div className="flex flex-col gap-2 text-center">
              <h1 className="font-serif text-3xl font-semibold leading-tight tracking-tight text-foreground">
                Entrar no Jurion
              </h1>
              <p className="text-base text-muted-foreground leading-normal">
                Acesse sua conta para continuar.
              </p>
            </div>
          </div>

          {accessUpdated ? (
            <p role="alert" tabIndex={-1} className="text-sm text-foreground">
              Seu acesso foi atualizado. Entre novamente para continuar.
            </p>
          ) : null}

          <form
            onSubmit={awaitingTwoFactor ? onTwoFactorSubmit : onSubmit}
            className="flex flex-col gap-4"
          >
            {awaitingTwoFactor ? (
              <div className="flex flex-col gap-2">
                <Label htmlFor="totp-code">Código de verificação</Label>
                <Input
                  id="totp-code"
                  name="totp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={totpCode}
                  onChange={(event) => setTotpCode(event.target.value)}
                  required
                  autoFocus
                />
              </div>
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="voce@escritorio.com.br"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="password">Senha</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Sua senha"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm p-1"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" strokeWidth={1.75} />
                      ) : (
                        <Eye className="h-4 w-4" strokeWidth={1.75} />
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                  Entrando…
                </>
              ) : awaitingTwoFactor ? (
                'Verificar código'
              ) : (
                'Entrar'
              )}
            </Button>
          </form>

          <div className="flex justify-center">
            {/* biome-ignore lint/a11y/useValidAnchor: in-page action ("esqueci senha") styled as a link; onClick drives it */}
            <a
              href="#esqueci-senha"
              onClick={onForgotPassword}
              className="text-sm text-foreground underline underline-offset-4 hover:text-primary"
            >
              Esqueceu sua senha?
            </a>
          </div>
        </div>
      </main>
      <Toaster />
    </div>
  );
}
